import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { Container } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import MultiSelectField from "../common/MultiSelectField";
import AuditLogPanel from "../common/AuditLogPanel";
import { C, tbl, reportingGlobalCSS, selectStyles, fieldLabel, dateInput } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader, TablePagination, usePagination } from "../reporting/ReportingShared";
import { isMerchantScoped, isReadOnly } from "../../constants/roles";
import { successToast, errorToast } from "../../shared/Toast";
import {
  getProductOptions,
  getRecommendationRules,
  saveRecommendationRules,
  getRecommendationStats,
  getRecommendationOverview,
  getRecommendationPreview,
  getRecommendationPerformance,
  getRecommendationConfig,
  saveRecommendationConfig,
  recomputeRecommendations,
} from "../../utils/recommendationApi";

/**
 * Product → Recommendations.
 *
 * What the pickup app suggests in the cart ("Complete your order") and on the product screen
 * ("Goes well with"), and how to steer it:
 *
 *   Curation     pin products to always show, block products that must never show, per slot
 *                and per product (or for every product), and preview what the app will get.
 *   Performance  what recommended lines actually sold, from real order lines.
 *   Settings     which rails are on, pilot branches, excluded categories, holdout, and — for a
 *                platform admin — which order sources the nightly job learns from.
 *   History      who changed what.
 *
 * Merchant-scoped like every other page. `operations` may look at everything and change
 * nothing; the API enforces the same rule independently.
 */

const SLOT_TABS = [
  { key: "cart", label: "Cart" },
  { key: "detail", label: "Product detail" },
  { key: "all", label: "Both" },
];

const PAGE_TABS = [
  { key: "rules", label: "Pins & blocks" },
  { key: "pairs", label: "Learned pairs" },
  { key: "sellers", label: "Best sellers" },
  { key: "performance", label: "Performance" },
  { key: "settings", label: "Settings" },
  { key: "history", label: "History" },
];

// Rule targets are products or app categories; one option list serves every picker. Option
// values are strings so the two kinds never collide: "p12" is product 12, "c12" category 12.
const GLOBAL_ANCHOR = { value: "global", label: "All products (applies everywhere)", kind: "global" };
const targetValue = (t) => (t.productId ? `p${t.productId}` : `c${t.appCategoryId}`);
const targetFromValue = (value) => (value.startsWith("c") ? { appCategoryId: Number(value.slice(1)) } : { productId: Number(value.slice(1)) });
const ruleTarget = (r) => (r.recProductId ? { productId: r.recProductId } : { appCategoryId: r.recAppCategoryId });
const ruleAnchor = (r) => (r.anchorProductId ? { productId: r.anchorProductId } : r.anchorAppCategoryId ? { appCategoryId: r.anchorAppCategoryId } : null);
const anchorValue = (a) => (a ? targetValue(a) : "global");

// Every card on this page fades in with a transform, which makes each card its own stacking
// context: an open menu cannot rise above the NEXT card, z-index or not, and the long product
// lists were cut off after three rows. Rendering the menu on <body> takes it out of the card.
// `auto` opens it upwards when there is no room below.
const MENU_PORTAL = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
  menuPlacement: "auto",
};

// Hours are the merchant's local time; the boundaries are the API's daypart constants.
const DAYPART_TABS = [
  { key: "all", label: "All day" },
  { key: "morning", label: "Morning · 5am–11am" },
  { key: "midday", label: "Midday · 11am–4pm" },
  { key: "evening", label: "Evening · 4pm–9pm" },
  { key: "late", label: "Late · 9pm–5am" },
];
const DAYPART_SHORT = { all: "All day", morning: "Morning", midday: "Midday", evening: "Evening", late: "Late" };

const SOURCE_META = {
  pin: { label: "Pinned", color: C.purple, bg: C.purpleLight },
  pair: { label: "Bought together", color: C.green, bg: C.greenLight },
  popular: { label: "Popular", color: C.blue, bg: C.blueLight },
};

const DROP_LABELS = {
  blocked_by_rule: "Blocked",
  blocked_category: "Blocked category",
  in_cart: "Already in cart",
  is_anchor: "Same product",
  not_visible: "Hidden / inactive / other brand",
  category_excluded: "Category excluded",
  unavailable_at_branch: "Unavailable at branch",
  snoozed_at_branch: "Snoozed at branch",
  diversity_cap: "Too many from one category",
  over_limit: "Beyond the rail length",
};

const GATE_LABELS = {
  feature_disabled: "The feature is switched off for this merchant (Edit Merchant → Product Recommendations). Customers see nothing yet.",
  slot_disabled: "This rail is switched off in Settings. Customers see nothing here yet.",
  branch_not_enabled: "This branch is not in the pilot branch list. Customers there see nothing.",
  holdout_running: "A holdout is running: some signed-in customers are deliberately shown nothing.",
};

const NATURE_LABELS = {
  normal: "Pickup app orders",
  loyalty: "POS orders with a loyalty customer",
  kiosk: "Kiosk orders",
  waiter: "Waiter orders",
  thirdParty: "Aggregator orders (Jahez, HungerStation…)",
};

const AUDIT_ACTION_LABELS = {
  pin: "Pinned",
  unpin: "Unpinned",
  block: "Blocked",
  unblock: "Unblocked",
  reorder: "Reordered pins",
  update: "Changed settings",
  recompute: "Started a recompute",
};

const btn = {
  padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer",
  background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white, boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = { ...btn, background: C.white, color: C.textMed, border: `1px solid ${C.border}`, boxShadow: "none" };
const numInput = { width: 80, padding: "6px 8px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13 };
const note = (color, bg) => ({ marginBottom: 12, padding: "10px 14px", borderRadius: 10, background: bg, color, fontWeight: 600, fontSize: 13 });

const Tabs = ({ tabs, value, onChange }) => (
  <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
    {tabs.map((t) => (
      <button
        key={t.key}
        onClick={() => onChange(t.key)}
        style={{
          padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
          border: t.key === value ? `1.5px solid ${C.coral}` : `1px solid ${C.border}`,
          background: t.key === value ? C.coralLight : C.white, color: t.key === value ? C.coral : C.text,
        }}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const pick = (options, ids) => ids.map((id) => options.find((o) => o.value === id)).filter(Boolean);

/** A product or category in a rule, as a chip. Categories are marked so the two never read alike. */
const TargetChip = ({ option }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, fontSize: 12, background: option.kind === "category" ? C.purpleLight : C.bg, color: C.text, border: `1px solid ${C.border}`, marginRight: 6, marginBottom: 6 }}>
    {option.kind === "category" && <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, letterSpacing: 0.3 }}>CATEGORY</span>}
    {option.kind === "category" ? option.name : option.label}
  </span>
);
const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "never");
const pct = (x) => `${Math.round((Number(x) || 0) * 1000) / 10}%`;
const money = (x) => (Number(x) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ymd = (d) => d.toISOString().slice(0, 10);

// ─── What the nightly job learned ─────────────────────────────────────────────

const clickableRow = (selected) => ({ cursor: "pointer", background: selected ? C.coralLight : undefined });

/** Every product with "bought together" pairs, busiest first. Clicking one opens it below. */
const LearnedProducts = ({ anchors, windowDays, loading, selectedId, onPick }) => {
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return anchors;
    return anchors.filter((a) => a.title?.toLowerCase().includes(needle) || a.arabicName?.includes(q.trim()));
  }, [anchors, q]);
  const { paged, page, limit, setPage, setLimit, total } = usePagination(rows, 10);

  return (
    <RCard title="Products with learned pairs">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: C.textMed }}>
          Products the system found "bought together" suggestions for, from real orders{windowDays ? ` in the last ${windowDays} days` : ""}, busiest first.
          Click one to see its pairs and preview it.
        </div>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search products…"
          style={{ ...dateInput, minWidth: 220 }}
        />
      </div>
      {loading ? <Loader /> : anchors.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: C.textMed }}>
          No pairs learned yet. They appear after the nightly job (or Settings → Recompute now) once there is enough order history.
        </div>
      ) : (
        <>
          <table style={tbl.table}>
            <thead>
              <tr>
                <th style={tbl.th}>Product</th>
                <th style={{ ...tbl.th, textAlign: "right" }}>Pairs</th>
                <th style={{ ...tbl.th, textAlign: "right" }}>Orders</th>
                <th style={tbl.th}>Top suggestion</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((a) => (
                <tr key={a.productId} style={clickableRow(a.productId === selectedId)} onClick={() => onPick(a)}>
                  <td style={tbl.td}>
                    {a.title}
                    {!a.isActive && <span style={{ marginLeft: 6 }}><Badge color={C.textMed} bg={C.bg}>Inactive</Badge></span>}
                  </td>
                  <td style={{ ...tbl.td, textAlign: "right" }}>{a.pairCount}</td>
                  <td style={{ ...tbl.td, textAlign: "right" }}>{a.anchorOrders.toLocaleString()}</td>
                  <td style={tbl.td}>
                    {a.top ? (
                      <>
                        {a.top.title}{" "}
                        <span style={{ color: C.textMed, fontSize: 12 }}>
                          {pct(a.top.confidence)} · {a.top.lift.toFixed(2)}×{a.top.daypart !== "all" ? ` · ${DAYPART_SHORT[a.top.daypart] || a.top.daypart}` : ""}
                        </span>
                      </>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination total={total} page={page} limit={limit} onPageChange={setPage} onLimitChange={setLimit} />
        </>
      )}
    </RCard>
  );
};

/** Top sellers per time of day — what pads a short rail (Popular). */
const TopSellers = ({ topSellers, loading }) => {
  const [daypart, setDaypart] = useState("all");
  const rows = topSellers?.[daypart] || [];

  return (
    <RCard title="Top sellers by time of day">
      <div style={{ fontSize: 12, color: C.textMed, marginBottom: 12 }}>
        When a rail has fewer learned pairs than it has room for, it is filled from these ("Popular"), current time of day first.
        Ranked by how many orders contain the product, not by pieces sold.
      </div>
      <Tabs tabs={DAYPART_TABS} value={daypart} onChange={setDaypart} />
      {loading ? <Loader /> : rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: C.textMed }}>
          {daypart === "all"
            ? "Not computed yet — the nightly job (or Settings → Recompute now) fills this."
            : "Too few orders at this time of day to rank on their own. The app uses the all-day list instead."}
        </div>
      ) : (
        <table style={tbl.table}>
          <thead>
            <tr>
              <th style={tbl.th}>#</th>
              <th style={tbl.th}>Product</th>
              <th style={{ ...tbl.th, textAlign: "right" }}>In % of orders</th>
              <th style={{ ...tbl.th, textAlign: "right" }}>Orders</th>
              <th style={{ ...tbl.th, textAlign: "right" }}>Pieces</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.productId}>
                <td style={tbl.td}>{r.rankPosition}</td>
                <td style={tbl.td}>
                  {r.title}
                  {!r.isActive && <span style={{ marginLeft: 6 }}><Badge color={C.textMed} bg={C.bg}>Inactive</Badge></span>}
                </td>
                <td style={{ ...tbl.td, textAlign: "right" }}>{r.basketOrders ? pct(r.orderCount / r.basketOrders) : "—"}</td>
                <td style={{ ...tbl.td, textAlign: "right" }}>{r.orderCount.toLocaleString()}</td>
                <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{r.quantity.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </RCard>
  );
};

// ─── Pins & blocks ────────────────────────────────────────────────────────────

/**
 * Every active rule, one row per (rail, anchor), so nobody has to open products one by one to
 * find out what is pinned or blocked. Built from the same GET /rules the tiles use.
 */
const ActiveRules = ({ rules, optionByValue, readOnly, onEdit, onRemove }) => {
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rules) {
      const anchor = ruleAnchor(r);
      const key = `${r.slot}|${anchorValue(anchor)}`;
      if (!map.has(key)) map.set(key, { slot: r.slot, anchor, pins: [], blocked: [] });
      const g = map.get(key);
      (r.ruleType === "pin" ? g.pins : g.blocked).push({ value: targetValue(ruleTarget(r)), sortOrder: r.sortOrder });
    }
    for (const g of map.values()) g.pins.sort((a, b) => a.sortOrder - b.sortOrder);
    const slotRank = { cart: 0, detail: 1, all: 2 };
    return [...map.values()].sort((a, b) => slotRank[a.slot] - slotRank[b.slot] || anchorValue(a.anchor).localeCompare(anchorValue(b.anchor)));
  }, [rules]);

  const chip = (value) => {
    const o = optionByValue.get(value) || { value, label: value, kind: value.startsWith("c") ? "category" : "product", name: value };
    return <TargetChip key={value} option={o} />;
  };
  const anchorCell = (anchor) => (anchor ? chip(anchorValue(anchor)) : <span style={{ color: C.textMed }}>All products</span>);

  return (
    <RCard title="Active rules">
      {groups.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20, color: C.textMed }}>No pins or blocks yet. Add the first one below.</div>
      ) : (
        <table style={tbl.table} data-testid="active-rules">
          <thead>
            <tr>
              <th style={tbl.th}>Rail</th>
              <th style={tbl.th}>When in cart / on screen</th>
              <th style={tbl.th}>Always show</th>
              <th style={tbl.th}>Never show</th>
              {!readOnly && <th style={tbl.th} />}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={`${g.slot}|${anchorValue(g.anchor)}`}>
                <td style={tbl.td}>{SLOT_TABS.find((t) => t.key === g.slot)?.label || g.slot}</td>
                <td style={tbl.td}>{anchorCell(g.anchor)}</td>
                <td style={tbl.td}>{g.pins.length ? g.pins.map((p) => chip(p.value)) : <span style={{ color: C.textLight }}>—</span>}</td>
                <td style={tbl.td}>{g.blocked.length ? g.blocked.map((b) => chip(b.value)) : <span style={{ color: C.textLight }}>—</span>}</td>
                {!readOnly && (
                  <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                    <button style={{ ...btnSecondary, padding: "6px 12px", marginRight: 6 }} onClick={() => onEdit(g)}>Edit</button>
                    <button style={{ ...btnSecondary, padding: "6px 12px", color: C.coral }} onClick={() => onRemove(g)}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </RCard>
  );
};

const PinsAndBlocks = ({ merchantId, readOnly, rules, targetOptions, branchOptions, onSaved, editTarget }) => {
  const [slot, setSlot] = useState("cart");
  // The "when" side: [] = all products; otherwise option values ("p12" / "c4"), several allowed.
  const [anchors, setAnchors] = useState([]);
  const [pins, setPins] = useState([]); // [{ value, sortOrder }]
  const [blocked, setBlocked] = useState([]); // [value]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewBranch, setPreviewBranch] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const editorRef = useRef(null);

  const optionByValue = useMemo(() => new Map(targetOptions.map((o) => [o.value, o])), [targetOptions]);
  const anchorOptions = useMemo(() => [GLOBAL_ANCHOR, ...targetOptions], [targetOptions]);
  const firstAnchor = anchors[0] ? targetFromValue(anchors[0]) : null;
  // Preview and "learned from orders" need one product; the first product anchor, if any.
  const anchorProductId = anchors.map(targetFromValue).find((a) => a.productId)?.productId ?? null;

  // Another tab (Learned pairs) or the rules table asked to edit one anchor's rules.
  useEffect(() => {
    if (!editTarget) return;
    setSlot(editTarget.slot || "cart");
    setAnchors(editTarget.anchor ? [targetValue(editTarget.anchor)] : []);
    setPreview(null);
    editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [editTarget]);

  // The editor shows the first anchor's rule set; with several anchors, saving writes that set
  // to every one of them.
  const load = useCallback(async () => {
    setLoading(true);
    const params = firstAnchor?.productId
      ? { anchorProductId: firstAnchor.productId }
      : firstAnchor?.appCategoryId
        ? { anchorAppCategoryId: firstAnchor.appCategoryId }
        : { anchorProductId: "null" };
    const data = await getRecommendationRules(merchantId, { slot, ...params });
    const rows = data?.rules || [];
    setPins(rows.filter((r) => r.ruleType === "pin").map((r) => ({ value: targetValue(ruleTarget(r)), sortOrder: r.sortOrder })));
    setBlocked(rows.filter((r) => r.ruleType === "block").map((r) => targetValue(ruleTarget(r))));
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, slot, anchors[0]]);

  useEffect(() => { load(); }, [load]);

  const pinValues = pins.map((p) => p.value);
  const nextSort = () => (pins.length ? Math.max(...pins.map((p) => p.sortOrder)) + 10 : 10);

  const onAnchorsChange = (selected) => {
    const values = (selected || []).map((o) => o.value);
    // "All products" is exclusive: picking it clears the others, picking anything clears it.
    const last = values[values.length - 1];
    setAnchors(last === "global" ? [] : values.filter((v) => v !== "global"));
    setPreview(null);
  };

  const onPinsChange = (selected) => {
    const values = (selected || []).map((o) => o.value);
    const kept = pins.filter((p) => values.includes(p.value));
    let sort = nextSort();
    const added = values.filter((v) => !pinValues.includes(v)).map((value) => { const row = { value, sortOrder: sort }; sort += 10; return row; });
    setPins([...kept, ...added]);
  };

  const validate = () => {
    if (pinValues.some((v) => blocked.includes(v))) return "A product or category cannot be both pinned and blocked.";
    const selfHit = anchors.filter((a) => a.startsWith("p")).find((a) => pinValues.includes(a) || blocked.includes(a));
    if (selfHit) return "A product cannot be pinned or blocked against itself.";
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) { errorToast(problem); return; }
    setSaving(true);
    const r = await saveRecommendationRules({
      merchantId,
      slot,
      anchors: anchors.length ? anchors.map(targetFromValue) : null,
      pins: pins.map((p) => ({ ...targetFromValue(p.value), sortOrder: p.sortOrder })),
      blocked: blocked.map(targetFromValue),
    });
    setSaving(false);
    if (r) { successToast("Recommendation rules saved"); onSaved(); }
  };

  const removeGroup = async (g) => {
    const name = g.anchor ? optionByValue.get(anchorValue(g.anchor))?.label || anchorValue(g.anchor) : "All products";
    if (!window.confirm(`Remove every pin and block for “${name}” on the ${SLOT_TABS.find((t) => t.key === g.slot)?.label} rail?`)) return;
    const r = await saveRecommendationRules({ merchantId, slot: g.slot, anchors: g.anchor ? [g.anchor] : null, pins: [], blocked: [] });
    if (r) { successToast("Rules removed"); onSaved(); if (g.slot === slot && anchorValue(g.anchor) === (anchors[0] || "global")) load(); }
  };

  const editGroup = (g) => {
    setSlot(g.slot);
    setAnchors(g.anchor ? [targetValue(g.anchor)] : []);
    setPreview(null);
    editorRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const runPreview = async () => {
    setPreviewing(true);
    const previewSlot = slot === "all" ? "cart" : slot;
    if (!anchorProductId) {
      setPreviewing(false);
      errorToast("Pick a product under “When this is in the cart” to preview — the preview needs a product in the cart.");
      return;
    }
    const data = await getRecommendationPreview(merchantId, {
      slot: previewSlot,
      anchorProductId,
      branchId: previewBranch?.value || undefined,
    });
    setPreview(data);
    setPreviewing(false);
  };

  const sortedPins = [...pins].sort((a, b) => a.sortOrder - b.sortOrder);
  const labelOf = (value) => optionByValue.get(value)?.label || value;

  return (
    <>
      <ActiveRules rules={rules} optionByValue={optionByValue} readOnly={readOnly} onEdit={editGroup} onRemove={removeGroup} />

      <div ref={editorRef} style={{ scrollMarginTop: 80 }} />
      <RCard title="Pins and blocks">
        <Tabs tabs={SLOT_TABS} value={slot} onChange={(s) => { setSlot(s); setPreview(null); }} />
        <div style={{ fontSize: 12, color: C.textMed, marginBottom: 14 }}>
          <b>Pin</b> = always show it, at the top, in your order. <b>Block</b> = never show it, whatever the data says.
          Both take products or whole categories: pinning a category leads the rail with that category’s best items; blocking one removes it entirely.
          A block always wins over a pin. Rules for “{SLOT_TABS.find((t) => t.key === slot).label}”
          {slot === "all" ? " apply to both rails." : " apply to that rail only."}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={fieldLabel}>When this is in the cart / on screen</div>
          <MultiSelectField
            {...MENU_PORTAL}
            options={anchorOptions}
            value={anchors.length ? pick(anchorOptions, anchors) : [GLOBAL_ANCHOR]}
            onChange={onAnchorsChange}
            isDisabled={readOnly}
            placeholder="Products or categories…"
          />
          {anchors.length > 1 && (
            <div style={{ fontSize: 12, color: C.textMed, marginTop: 6 }}>
              Showing the rules of {labelOf(anchors[0])}. Saving applies the pins and blocks below to all {anchors.length} selected.
            </div>
          )}
        </div>

        {loading ? <Loader /> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
            <div>
              <div style={fieldLabel}>Always show (pins)</div>
              <MultiSelectField {...MENU_PORTAL} options={targetOptions} value={pick(targetOptions, pinValues)} onChange={onPinsChange} isDisabled={readOnly} placeholder="Products or categories to pin…" />
              {pinValues.some((v) => v.startsWith("c")) && (
                <div style={{ ...note(C.text, C.purpleLight), fontWeight: 400, marginTop: 10, fontSize: 12 }}>
                  <b>How a pinned category picks its products.</b> Not the whole category: the rail leads with the items of that category that
                  earned a place — first the ones your customers buy together with what is in the cart, then that category’s best sellers at
                  the current time of day — up to the number of places set in Settings › Rails. A brand-new product with no orders yet is
                  not among them; pin it by name to show it.
                </div>
              )}
              {sortedPins.length > 0 && (
                <table style={{ ...tbl.table, marginTop: 10 }}>
                  <thead><tr><th style={tbl.th}>Pinned</th><th style={{ ...tbl.th, width: 110 }}>Order</th></tr></thead>
                  <tbody>
                    {sortedPins.map((p) => (
                      <tr key={p.value}>
                        <td style={tbl.td}>{labelOf(p.value)}</td>
                        <td style={tbl.td}>
                          <input
                            type="number"
                            style={numInput}
                            value={p.sortOrder}
                            disabled={readOnly}
                            onChange={(e) => setPins(pins.map((x) => (x.value === p.value ? { ...x, sortOrder: parseInt(e.target.value, 10) || 0 } : x)))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <div style={fieldLabel}>Never show (blocks)</div>
              <MultiSelectField {...MENU_PORTAL} options={targetOptions} value={pick(targetOptions, blocked)} onChange={(s) => setBlocked((s || []).map((o) => o.value))} isDisabled={readOnly} placeholder="Products or categories to block…" />
            </div>
          </div>
        )}

        {!readOnly && (
          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
            {/* Disabled while the anchor's rules are still loading: saving then would write the
                previous anchor's pins onto this one. */}
            <button style={btn} onClick={save} disabled={saving || loading}>{saving ? "Saving…" : "Save rules"}</button>
          </div>
        )}
      </RCard>

      <RCard title="Preview — what the app shows right now">
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ minWidth: 260 }}>
            <div style={fieldLabel}>Branch (availability differs by branch)</div>
            <Select {...MENU_PORTAL} styles={selectStyles} isClearable options={branchOptions} value={previewBranch} onChange={setPreviewBranch} placeholder="Any branch" />
          </div>
          <button style={btnSecondary} onClick={runPreview} disabled={previewing}>{previewing ? "Loading…" : "Show preview"}</button>
          {anchorProductId && <span style={{ fontSize: 12, color: C.textMed }}>Cart: {labelOf(`p${anchorProductId}`)}</span>}
        </div>
        {preview && (
          <>
            {(preview.gates || []).map((g) => <div key={g} style={note(C.orange, C.orangeLight)}>{GATE_LABELS[g] || g}</div>)}
            {preview.products.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: C.textMed }}>
                Not enough order history yet for this product. Pinned items will still show.
              </div>
            ) : (
              <table style={tbl.table}>
                <thead><tr><th style={tbl.th}>#</th><th style={tbl.th}>Product</th><th style={tbl.th}>Why</th><th style={{ ...tbl.th, textAlign: "right" }}>Price</th><th style={{ ...tbl.th, textAlign: "right" }}>Score</th></tr></thead>
                <tbody>
                  {preview.products.map((p, i) => {
                    const m = SOURCE_META[p.source] || { label: p.source, color: C.textMed, bg: C.bg };
                    return (
                      <tr key={p.productId}>
                        <td style={tbl.td}>{i + 1}</td>
                        <td style={tbl.td}>{p.title}</td>
                        <td style={tbl.td}><Badge color={m.color} bg={m.bg}>{m.label}</Badge></td>
                        <td style={{ ...tbl.td, textAlign: "right" }}>{money(p.price)}</td>
                        <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{p.score ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {preview.dropped?.length > 0 && (
              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: "pointer", fontSize: 13, color: C.textMed, fontWeight: 600 }}>
                  {preview.dropped.length} candidate{preview.dropped.length === 1 ? "" : "s"} filtered out — why
                </summary>
                <table style={{ ...tbl.table, marginTop: 8 }}>
                  <tbody>
                    {preview.dropped.map((d, i) => (
                      <tr key={`${d.productId}-${i}`}>
                        <td style={tbl.td}>{d.title || `#${d.productId}`}</td>
                        <td style={tbl.td}><Badge color={C.textMed} bg={C.bg}>{DROP_LABELS[d.reason] || d.reason}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 10 }}>
              Time of day: {preview.daypart} · Data computed: {fmtDate(preview.meta?.computedAt)}{preview.meta?.stale ? " (stale — the nightly job has not refreshed it)" : ""}
            </div>
          </>
        )}
      </RCard>
    </>
  );
};

// ─── Learned pairs ────────────────────────────────────────────────────────────

const LearnedPairs = ({ merchantId, overview, overviewLoading, productOptions, onEditRules }) => {
  const [selected, setSelected] = useState(null); // an overview anchor row
  const [stats, setStats] = useState([]);
  const detailRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!selected) { setStats([]); return undefined; }
    getRecommendationStats(merchantId, selected.productId).then((d) => alive && setStats(d?.pairs || []));
    return () => { alive = false; };
  }, [merchantId, selected]);

  const pickLearned = (a) => {
    setSelected(a);
    detailRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <LearnedProducts
        anchors={overview.anchors || []}
        windowDays={overview.windowDays}
        loading={overviewLoading}
        selectedId={selected?.productId ?? null}
        onPick={pickLearned}
      />
      <div ref={detailRef} style={{ scrollMarginTop: 80 }} />
      {selected && (
        <RCard title={`Learned from orders — bought together with ${selected.title}`}>
          {stats.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: C.textMed }}>No pairs learned for this product yet.</div>
          ) : (
            <table style={tbl.table}>
              <thead><tr><th style={tbl.th}>Time of day</th><th style={tbl.th}>Product</th><th style={{ ...tbl.th, textAlign: "right" }}>Together in</th><th style={{ ...tbl.th, textAlign: "right" }}>Chance</th><th style={{ ...tbl.th, textAlign: "right" }}>Lift</th></tr></thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.id}>
                    <td style={tbl.td}>{DAYPART_SHORT[s.daypart] || s.daypart}</td>
                    <td style={tbl.td}>{s.recProduct?.title || `#${s.recProductId}`}</td>
                    <td style={{ ...tbl.td, textAlign: "right" }}>{s.pairOrders} orders</td>
                    <td style={{ ...tbl.td, textAlign: "right" }}>{pct(s.confidence)}</td>
                    <td style={{ ...tbl.td, textAlign: "right" }}>{Number(s.lift).toFixed(2)}×</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
            <button style={btnSecondary} onClick={() => onEditRules({ slot: "cart", anchor: { productId: selected.productId } })}>
              Pin or block for {productOptions.find((o) => o.value === `p${selected.productId}`)?.label || selected.title} →
            </button>
          </div>
        </RCard>
      )}
    </>
  );
};

// ─── Performance ──────────────────────────────────────────────────────────────

const Performance = ({ merchantId, branchOptions }) => {
  const [startDate, setStartDate] = useState(ymd(new Date(Date.now() - 29 * 86400000)));
  const [endDate, setEndDate] = useState(ymd(new Date()));
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setData(await getRecommendationPerformance(merchantId, { startDate, endDate, branchId: branch?.value || undefined, groupBy: "arm" }));
    setLoading(false);
  }, [merchantId, startDate, endDate, branch]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.byProduct || [], [data]);
  const { paged, page, limit, setPage, setLimit, total } = usePagination(rows, 20);
  const s = data?.summary;
  const arms = data?.arms;

  return (
    <>
      <RCard>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div><div style={fieldLabel}>From</div><input type="date" style={dateInput} value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div><div style={fieldLabel}>To</div><input type="date" style={dateInput} value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
          <div style={{ minWidth: 240 }}><div style={fieldLabel}>Branch</div><Select {...MENU_PORTAL} styles={selectStyles} isClearable options={branchOptions} value={branch} onChange={setBranch} placeholder="All branches" /></div>
        </div>
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 10 }}>
          Pickup-app orders only. Revenue is line totals before order-level coupons. How often a rail was seen or tapped is not tracked.
        </div>
      </RCard>

      {loading && !data ? <Loader /> : s && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
            <Tile label="Orders with a recommended item" value={pct(s.attachRate)} sub={`${s.ordersWithRec} of ${s.orders} orders`} />
            <Tile label="Revenue from recommendations" value={money(s.recRevenue)} sub={`${s.recUnits} items`} />
            <Tile label="Recommended lines" value={s.recLines} sub={s.recRewardLines ? `+${s.recRewardLines} redeemed as rewards` : "order lines"} />
          </div>

          {arms?.treatment && (
            <RCard title="Holdout — the real (incremental) effect">
              <table style={tbl.table}>
                <thead><tr><th style={tbl.th}></th><th style={{ ...tbl.th, textAlign: "right" }}>Customers</th><th style={{ ...tbl.th, textAlign: "right" }}>Orders</th><th style={{ ...tbl.th, textAlign: "right" }}>Items / order</th><th style={{ ...tbl.th, textAlign: "right" }}>Avg order</th></tr></thead>
                <tbody>
                  {[["Shown recommendations", arms.treatment], ["Held back (control)", arms.control]].map(([label, a]) => (
                    <tr key={label}>
                      <td style={tbl.td}>{label}</td>
                      <td style={{ ...tbl.td, textAlign: "right" }}>{a.customers}</td>
                      <td style={{ ...tbl.td, textAlign: "right" }}>{a.orders}</td>
                      <td style={{ ...tbl.td, textAlign: "right" }}>{a.itemsPerOrder}</td>
                      <td style={{ ...tbl.td, textAlign: "right" }}>{money(a.aov)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 13, color: C.text, marginTop: 12 }}>
                Difference: <b>{arms.incremental.itemsPerOrder >= 0 ? "+" : ""}{arms.incremental.itemsPerOrder}</b> items per order,{" "}
                <b>{arms.incremental.aov >= 0 ? "+" : ""}{money(arms.incremental.aov)}</b> per order
                {" "}(≈ {money(arms.incremental.revenue)} over the period). Since {fmtDate(arms.from)}.
              </div>
              {arms.contamination > 0 && <div style={{ ...note(C.orange, C.orangeLight), marginTop: 10 }}>{arms.contamination} held-back order(s) still contain a recommended item — worth a look.</div>}
            </RCard>
          )}

          <RCard title="By product">
            {rows.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: C.textMed }}>No recommended items sold in this period.</div>
            ) : (
              <>
                <table style={tbl.table}>
                  <thead><tr><th style={tbl.th}>Product</th><th style={tbl.th}>Placement</th><th style={tbl.th}>Reason</th><th style={{ ...tbl.th, textAlign: "right" }}>Times added</th><th style={{ ...tbl.th, textAlign: "right" }}>Revenue</th></tr></thead>
                  <tbody>
                    {paged.map((r) => {
                      const m = SOURCE_META[r.reason] || { label: r.reason, color: C.textMed, bg: C.bg };
                      return (
                        <tr key={`${r.productId}-${r.placement}-${r.reason}`}>
                          <td style={tbl.td}>{r.title || `#${r.productId}`}</td>
                          <td style={tbl.td}>{r.placement === "detail" ? "Product detail" : "Cart"}</td>
                          <td style={tbl.td}><Badge color={m.color} bg={m.bg}>{m.label}</Badge></td>
                          <td style={{ ...tbl.td, textAlign: "right" }}>{r.units}</td>
                          <td style={{ ...tbl.td, textAlign: "right" }}>{money(r.revenue)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <TablePagination total={total} page={page} limit={limit} onPageChange={setPage} onLimitChange={setLimit} />
              </>
            )}
          </RCard>
        </>
      )}
    </>
  );
};

// ─── Settings ─────────────────────────────────────────────────────────────────

const Settings = ({ merchantId, readOnly, onSaved }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const d = await getRecommendationConfig(merchantId);
    setData(d);
    if (d) {
      setForm({
        cart: d.config.slots.cart,
        detail: d.config.slots.detail,
        allBranches: d.config.enabledBranchIds === null,
        branchIds: d.config.enabledBranchIds || [],
        popularityFallback: d.config.popularityFallback,
        pinnedCategorySlots: d.config.pinnedCategorySlots,
        excludedAppCategoryIds: d.config.excludedAppCategoryIds,
        holdoutEnabled: d.config.holdout.enabled,
        holdoutPercent: d.config.holdout.percent,
        natures: d.config.basketOrderNatures,
      });
    }
  }, [merchantId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !form) return <Loader />;

  const canEdit = !readOnly && data.permissions?.canEdit;
  const canTrain = canEdit && data.permissions?.canEditTraining;
  const branchOptions = data.branches.map((b) => ({ value: b.id, label: b.active ? b.name : `${b.name} (inactive)` }));
  const categoryOptions = data.appCategories.map((c) => ({ value: c.id, label: c.name }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (canTrain && form.natures.length === 0) { errorToast("Pick at least one order source to learn from."); return; }
    const holdoutChanged = form.holdoutEnabled !== data.config.holdout.enabled || Number(form.holdoutPercent) !== data.config.holdout.percent;
    const changes = {
      slots: { cart: form.cart, detail: form.detail },
      enabledBranchIds: form.allBranches ? null : form.branchIds,
      popularityFallback: form.popularityFallback,
      pinnedCategorySlots: Number(form.pinnedCategorySlots),
      excludedAppCategoryIds: form.excludedAppCategoryIds,
      ...(holdoutChanged && {
        holdout: {
          enabled: form.holdoutEnabled,
          percent: Number(form.holdoutPercent),
          // Changing the split of a running holdout is a new experiment: new salt, new start.
          ...(data.config.holdout.enabled && form.holdoutEnabled && { salt: `v${Date.now()}`, startedAt: new Date().toISOString() }),
        },
      }),
      ...(canTrain && { basketOrderNatures: form.natures }),
    };
    setSaving(true);
    const r = await saveRecommendationConfig(merchantId, changes);
    setSaving(false);
    if (r) { successToast("Settings saved"); await load(); onSaved(); }
  };

  const recompute = async () => {
    const r = await recomputeRecommendations(merchantId);
    if (r) successToast("Recompute started — results appear within a few minutes");
  };

  const toggle = (label, key, help) => (
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, cursor: canEdit ? "pointer" : "default" }}>
      <input type="checkbox" checked={!!form[key]} disabled={!canEdit} onChange={(e) => set({ [key]: e.target.checked })} style={{ marginTop: 3 }} />
      <span><b style={{ color: C.text }}>{label}</b>{help && <div style={{ fontSize: 12, color: C.textMed }}>{help}</div>}</span>
    </label>
  );

  return (
    <>
      {!data.enableProductRecommendations && (
        <div style={note(C.orange, C.orangeLight)}>
          The feature is switched off for this merchant. A platform admin turns it on in Edit Merchant → Product Recommendations. You can set everything up here first.
        </div>
      )}

      <RCard title="Rails">
        {toggle("Cart — “Complete your order”", "cart", "Shown under the items in the bag, with a quick-add button.")}
        {toggle("Product detail — “Goes well with”", "detail", "Shown on the product screen. Turn on after the cart pilot is reviewed.")}
        {toggle("Fill short rails with popular items", "popularityFallback", "When there are too few learned pairs, top up with what sells most at this time of day (never from the same category).")}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
          <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>Places for pinned categories</span>
          <input type="number" min={0} max={12} style={numInput} value={form.pinnedCategorySlots} disabled={!canEdit} onChange={(e) => set({ pinnedCategorySlots: e.target.value })} aria-label="Places for pinned categories" />
          <span style={{ fontSize: 13, color: C.textMed }}>of the rail’s {data.config.defaultLimit}</span>
        </div>
        <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>
          When a rule pins a whole category, this many places go to that category’s best items first; the rest come from the normal ranking. If the category has fewer items that earned a place, the rest fills normally.
        </div>
      </RCard>

      <RCard title="Pilot branches">
        <label style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input type="radio" checked={form.allBranches} disabled={!canEdit} onChange={() => set({ allBranches: true })} /> All branches
        </label>
        <label style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input type="radio" checked={!form.allBranches} disabled={!canEdit} onChange={() => set({ allBranches: false })} /> Only these branches
        </label>
        {!form.allBranches && (
          <>
            <MultiSelectField {...MENU_PORTAL} options={branchOptions} value={pick(branchOptions, form.branchIds)} onChange={(s) => set({ branchIds: (s || []).map((o) => o.value) })} isDisabled={!canEdit} placeholder="Pick branches…" />
            {form.branchIds.length === 0 && <div style={{ fontSize: 12, color: C.orange, marginTop: 6 }}>No branch selected — customers will see nothing anywhere.</div>}
          </>
        )}
      </RCard>

      <RCard title="Excluded categories">
        <div style={{ fontSize: 12, color: C.textMed, marginBottom: 10 }}>
          Products in these categories are never suggested (merchandise, coffee bags, gift cards…). They can still trigger suggestions when they are in the cart, and a product you pin explicitly still shows. New products added to these categories are covered automatically.
        </div>
        <MultiSelectField {...MENU_PORTAL} options={categoryOptions} value={pick(categoryOptions, form.excludedAppCategoryIds)} onChange={(s) => set({ excludedAppCategoryIds: (s || []).map((o) => o.value) })} isDisabled={!canEdit} placeholder="Pick categories…" />
      </RCard>

      <RCard title="Holdout (measure the real effect)">
        {toggle("Hold back some signed-in customers", "holdoutEnabled", "They see no recommendations, so the Performance tab can compare both groups. Guests always see the rail.")}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: C.textMed }}>Share held back</span>
          <input type="number" min={0} max={50} style={numInput} value={form.holdoutPercent} disabled={!canEdit} onChange={(e) => set({ holdoutPercent: e.target.value })} />
          <span style={{ fontSize: 13, color: C.textMed }}>%</span>
        </div>
        {data.config.holdout.enabled && <div style={{ fontSize: 12, color: C.textMed, marginTop: 8 }}>Running since {fmtDate(data.config.holdout.startedAt)}. Changing the share starts a new comparison.</div>}
      </RCard>

      <RCard title="Training data — which orders the system learns from">
        <div style={{ fontSize: 12, color: C.textMed, marginBottom: 10 }}>
          Suggestions are always shown in the pickup app, but they can be learned from other order sources. Counts are orders in the last {data.config.windowDays} days. Changes apply from the next nightly run.
          {!canTrain && " Only a platform admin can change this."}
        </div>
        {Object.keys(NATURE_LABELS).map((n) => (
          <label key={n} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={form.natures.includes(n)}
              disabled={!canTrain}
              onChange={(e) => set({ natures: e.target.checked ? [...form.natures, n] : form.natures.filter((x) => x !== n) })}
            />
            <span style={{ flex: 1 }}>{NATURE_LABELS[n]}</span>
            <span style={{ color: C.textMed, fontSize: 12 }}>{(data.natureCounts?.[n] || 0).toLocaleString()} orders</span>
          </label>
        ))}
        {canTrain && (
          <div style={{ marginTop: 12 }}>
            <button style={btnSecondary} onClick={recompute} disabled={data.recomputeRunning}>
              {data.recomputeRunning ? "A recompute is running…" : "Recompute now"}
            </button>
          </div>
        )}
      </RCard>

      {canEdit && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
          <button style={btn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save settings"}</button>
        </div>
      )}
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const Recommendations = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const merchantId = isMerchantScoped(userType) ? loggedInMerchantId : reduxSelectedMerchant;
  const readOnly = isReadOnly(userType);
  const noMerchant = !merchantId || merchantId === "all";

  const [tab, setTab] = useState("rules");
  const [products, setProducts] = useState([]);
  const [rules, setRules] = useState([]);
  const [ruleStubs, setRuleStubs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [appCategories, setAppCategories] = useState([]);
  const [freshness, setFreshness] = useState(null);
  const [ruleCount, setRuleCount] = useState({ pins: 0, blocks: 0, anchors: 0 });
  const [auditKey, setAuditKey] = useState(0);
  const [overview, setOverview] = useState({ windowDays: null, anchors: [], topSellers: {} });
  const [overviewLoading, setOverviewLoading] = useState(true);
  // Set by "Pin or block for X" on the Learned pairs tab: opens the editor on that product.
  const [editTarget, setEditTarget] = useState(null);

  // The catalogue is the expensive call and only changes when the menu syncs, so it loads once
  // per merchant. Rules, stats and settings are what a save changes, so only those reload after
  // one — the catalogue used to come back with modifiers and brands on every save.
  const loadProducts = useCallback(async () => {
    if (noMerchant) return;
    setProducts(await getProductOptions(merchantId));
  }, [merchantId, noMerchant]);

  const loadMeta = useCallback(async () => {
    if (noMerchant) return;
    const [rules, stats, cfg] = await Promise.all([
      getRecommendationRules(merchantId),
      getRecommendationStats(merchantId),
      getRecommendationConfig(merchantId),
    ]);
    const ruleRows = rules?.rules || [];
    setRules(ruleRows);
    // The catalogue is active-only; a rule may point at a product switched off since. Keep it
    // selectable, or loading and saving a rule set would silently drop that pin.
    const stubs = new Map();
    for (const r of ruleRows) {
      for (const p of [r.recProduct, r.anchorProduct]) if (p) stubs.set(p.id, p);
    }
    setRuleStubs([...stubs.values()]);
    setRuleCount({
      pins: ruleRows.filter((r) => r.ruleType === "pin").length,
      blocks: ruleRows.filter((r) => r.ruleType === "block").length,
      anchors: new Set(ruleRows.map((r) => `${r.slot}|${anchorValue(ruleAnchor(r))}`)).size,
    });
    setFreshness(stats?.freshness || null);
    setBranches(cfg?.branches || []);
    setAppCategories(cfg?.appCategories || []);
  }, [merchantId, noMerchant]);

  const loadOverview = useCallback(async () => {
    if (noMerchant) return;
    setOverviewLoading(true);
    const d = await getRecommendationOverview(merchantId);
    setOverview(d || { windowDays: null, anchors: [], topSellers: {} });
    setOverviewLoading(false);
  }, [merchantId, noMerchant]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Products as "p<id>", inactive ones referenced by a rule included, labelled.
  const productOptions = useMemo(() => {
    const active = new Set(products.map((p) => p.id));
    return [
      ...products.map((p) => ({ value: `p${p.id}`, label: p.title, kind: "product" })),
      ...ruleStubs.filter((p) => !active.has(p.id)).map((p) => ({ value: `p${p.id}`, label: `${p.title} (inactive)`, kind: "product" })),
    ];
  }, [products, ruleStubs]);
  // Categories first so they are found without scrolling; "Category: " keeps a category and a
  // product of the same name apart in the picker.
  const targetOptions = useMemo(
    () => [
      ...appCategories.map((c) => ({ value: `c${c.id}`, label: `Category: ${c.name}${c.active === false ? " (inactive)" : ""}`, name: c.name, kind: "category" })),
      ...productOptions,
    ],
    [appCategories, productOptions]
  );
  const branchOptions = useMemo(() => branches.map((b) => ({ value: b.id, label: b.name })), [branches]);

  const onSaved = () => { loadMeta(); setAuditKey((k) => k + 1); };
  const onEditRules = (target) => { setEditTarget({ ...target, at: Date.now() }); setTab("rules"); };

  return (
    <Fragment>
      <Breadcrumb title="Recommendations" parent="Product" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {noMerchant ? (
            <RCard><div style={{ textAlign: "center", padding: 40, color: C.textMed }}>Select a merchant to manage its product recommendations.</div></RCard>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
                <Tile label="Pinned" value={ruleCount.pins} sub="always-show rules" />
                <Tile label="Blocked" value={ruleCount.blocks} sub="never-show rules" />
                <Tile label="Learned pairs" value={Number(freshness?.pairRows || 0).toLocaleString()} sub={`${freshness?.anchorsWithPairs || 0} products with suggestions`} />
                <Tile label="Last computed" value={freshness?.popularityComputedAt ? new Date(freshness.popularityComputedAt).toLocaleDateString() : "—"} sub={freshness?.popularityComputedAt ? new Date(freshness.popularityComputedAt).toLocaleTimeString() : "the nightly job has not run yet"} />
              </div>

              <Tabs tabs={PAGE_TABS} value={tab} onChange={setTab} />

              {tab === "rules" && (
                <PinsAndBlocks
                  merchantId={merchantId}
                  readOnly={readOnly}
                  rules={rules}
                  targetOptions={targetOptions}
                  branchOptions={branchOptions}
                  onSaved={onSaved}
                  editTarget={editTarget}
                />
              )}
              {tab === "pairs" && <LearnedPairs merchantId={merchantId} overview={overview} overviewLoading={overviewLoading} productOptions={productOptions} onEditRules={onEditRules} />}
              {tab === "sellers" && <TopSellers topSellers={overview.topSellers} loading={overviewLoading} />}
              {tab === "performance" && <Performance merchantId={merchantId} branchOptions={branchOptions} />}
              {tab === "settings" && <Settings merchantId={merchantId} readOnly={readOnly} onSaved={onSaved} />}
              {tab === "history" && (
                <AuditLogPanel
                  key={auditKey}
                  merchantId={merchantId}
                  module="recommendations"
                  entityTypes={["recommendation_rule", "recommendation_config"]}
                  actionLabels={AUDIT_ACTION_LABELS}
                  title="Changes to recommendations"
                />
              )}
            </>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export default Recommendations;
